// Cave light state and the shared nearest-light budget for terrain and companion lights.
import * as THREE from 'three';
import { hfCarvedAt, hfSurfaceYAt } from './heightfieldData';
import { SKY_NIGHT } from './sky/skyLight';

const DEPTH_ON = 1.6;            // 현재 지표보다 이만큼 아래면 "땅속"
const FADE = 3.5;                // 켜짐/꺼짐 보간 속도(초⁻¹) — 입구에서 깜빡이지 않게

// [폐기 2026-07-28] 여기 있던 "지하 가시성"(카메라→플레이어 원통을 도려내 지형을 뚫는 것)은
//   제거했다. 디더로 녹여도 화면 한가운데 뚫린 원은 **스나이퍼 스코프처럼 보여** 게임을 깨뜨린다
//   (명관님 판단). 대안은 지형을 자르는 게 아니라 **카메라를 굴 안으로 당기는 것** — 3인칭 표준.

// Local companion lights share the existing nearest-light budget and shader uniforms.
const PET_LIGHTS = new Map();
export const setPetLight = (id, light) => {
  if (id == null || !light) return;
  PET_LIGHTS.set(String(id), light);
};
export const clearPetLight = (id) => {
  if (id == null) return;
  PET_LIGHTS.delete(String(id));
};
export const petLightCandidates = (position, radius) => {
  const ambientDark = Math.max(SKY_NIGHT.value, UNDER.value);
  if (ambientDark < 0.05) return [];
  const r2 = radius * radius;
  const out = [];
  for (const [key, l] of PET_LIGHTS) {
    const dx = l.x - position.x, dy = l.y - position.y, dz = l.z - position.z;
    const d2 = dx * dx + dz * dz;
    if (d2 > r2) continue;
    out.push({ ...l, key: `pet:${key}`, d2, d3: d2 + dy * dy, up: dy, lamp: true, pet: true });
  }
  return out;
};

// 「지금 땅속인가」를 셰이더에 넘기는 공유 uniform (0..1, 입구에서 부드럽게 보간).
// 지형 셰이더가 이걸로 **지상 전용 항을 끈다** — 원경 디테일 페이드(밝은 평균색으로 수렴)와
// 구름 그림자(fbm 4~6옥타브)다. 둘 다 굴 안에서는 ①보일 리 없고 ②오히려 해롭다:
// 페이드는 먼 벽을 회녹색으로 물들여 깊이를 지우고, 구름 그림자는 순수 낭비다.
// 「굴 안에서는 지상 레이어를 끈다」의 셰이더 몫 — 프래그먼트에서 가장 비싼 두 항을 뺀다.
export const UNDER = { value: 0 };

// ── 고정 광원 (발광 결정) — 2026-08-02 ──────────────────────────────────────
// [왜 필요한가] 동굴에 발광 블록을 아무리 깔아도 **바위는 안 밝아졌다.** 지형이 unlit
//   ShaderMaterial 이라 BuildLights 의 pointLight 가 블록·몬스터만 비추고 지형에는 한 줄도
//   안 먹기 때문이다. 그래서 "조명을 더 깔았는데 여전히 새까맣다"가 된다 —
//   고정 조명과 펫 조명이 같은 유니폼 경로를 공유하도록 K개 광원을 관리한다.
// [예산제] 최근접 K개만. 라이트 수 × 재질 수 곱셈 폭발을 막는 표준 처방이고, 프래그먼트
//   비용도 거리 계산 K회뿐이다(텍스처 탭 0 — 샘플러 16개 한도와 무관).
// [누가 채우나] BuildLights 가 0.6초 주기로 최근접 재정렬을 이미 돌고 있다. 스캐너를 둘로
//   두면 언젠가 갈라지므로 **그 한 곳**이 이 배열도 같이 채운다.
// 슬롯 수 = 캐릭터 둘레에서 동시에 켜지는 빛 수. 수정 동굴처럼 빛 자리가 촘촘하면(반경 14~17m 빛 수백 곳)
//   적을 때 «가까이 가야만 켜진다». 그중 CAVE_LIGHT_UP 개는 머리 위(천장) 전용 예약(BuildLights).
//   비용은 프래그먼트 거리 계산 K회뿐 — 텍스처 탭 0 이라 샘플러 16개 한도와 무관하다.
//   ⚠ 셰이더 배열 길이(HeightfieldPoC HF_FRAG)는 이 상수를 문자열 보간으로 읽는다. 숫자 중복 금지.
export const CAVE_LIGHT_MAX = 24;
export const CAVE_LIGHT_UP = 6;   // 그중 「플레이어보다 위」 전용 슬롯 수
export const CAVE_LIGHTS = {
  value: Array.from({ length: CAVE_LIGHT_MAX }, () => new THREE.Vector4(0, -9999, 0, 0)),
};
export const CAVE_LIGHT_COLOR = { value: new THREE.Color(1.0, 0.80, 0.46) };
export const CAVE_LIGHT_R = 17;   // m — 결정 하나가 밝히는 반경(기본값. 광원이 제 값을 주면 그게 이긴다)
// 땅속 빛웅덩이 모양 — 반경 ×R 로 멀리 닿게 하고, 감쇠를 a² → a³·CORE 로 바꿔 등 밑은 더 밝고 등 사이는 더 어둡다.
// 셰이더(terrainBlockMat LAMP_FRAG · blockArrayMat CAVE_LAMP)가 CAVE_POOL_K(0=지상 a², 1=땅속 a³·CORE)로 섞는다.
export const CAVE_POOL_R = 1.35;
export const CAVE_POOL_CORE = 1.8;
export const CAVE_POOL_K = { get value() { return UNDER.value; } };

// ── 광원별 색 ──────────────────────────────────────────────────
// 여태 색이 **전 슬롯 공용 하나**였다. 발광 결정만 있을 땐 맞는 단순화였는데, 여기에
// 마을 가로등(주황)·수정(시안)·룬돌(보라)이 같이 들어오면 한 색으로는 못 그린다.
// vec3 배열 하나 늘리는 비용은 프래그먼트에서 0 이다(텍스처 탭 0 — 샘플러 한도와 무관).
// 평평한 Float32Array 를 쓰는 이유: three 의 setValueV3fArray 가 그대로 업로드한다(변환 없음).
export const CAVE_LIGHT_RGB = { value: new Float32Array(CAVE_LIGHT_MAX * 3) };

// ── 광원별 게이트 — 「낮의 지면은 해가 이미 밝히고 있다」 ──
// [증상] 대낮에 던전 입구 마당이 통째로 노랗게 탔다(명관님 제보 이미지 1). 잔디 알베도가
//   밝은데 거기에 `col += col·lit·2.8` 이 얹히니 노출 과다가 된다.
// [원인] 이 경로는 **동굴용으로 설계됐는데 입력이 온 세상 light 블록**이다. BuildLights 는
//   반경 52m 안의 light 블록을 전부 담고, 셰이더는 그게 땅속인지 한낮의 잔디밭인지 모른다.
//   갱구 표식등·비석 룬처럼 **지상에 놓은 등**이 정확히 이 경우다.
// [고침] 광원마다 게이트를 하나 실어 보낸다 — 땅속 광원은 언제나 1(굴 안엔 낮이 없다),
//   지상 광원은 **밤 정도**(SKY_NIGHT). 낮에는 0 이라 지면이 안 타고, 밤에는 등불 웅덩이가
//   그대로 남아 「저기가 입구다」가 살아 있다.
// [왜 셰이더가 아니라 여기서 재나] 프래그먼트는 「이 픽셀이 하늘을 보나」를 모른다. 광원의
//   좌표는 JS 가 쥐고 있고 hfSurfaceYAt 로 정확히 답할 수 있다 — 0.6초에 한 번이면 충분하다.
export const CAVE_LIGHT_GATE = { value: new Array(CAVE_LIGHT_MAX).fill(0) };

// 수면에 비치는 틈새 발광 점 — 벽을 비추지 않고 «물에만» 점으로 맺힌다(동굴 수면 셰이더 전용).
// xyz = 위치, w = 밝기(음수 = 라벤더, 양수 = 청람, 0 = 끝). 채우는 쪽: CaveCrystalFx 0.4초 재정렬.
export const CAVE_GLOW_MAX = 32;
export const CAVE_GLOWS = {
  value: Array.from({ length: CAVE_GLOW_MAX }, () => new THREE.Vector4(0, -9999, 0, 0)),
};

/** 최근접 광원 목록을 유니폼에 싣는다. list = [{x,y,z, r?, rgb?, gain?}] (가까운 순, K개 이하)
 *
 *  r    — 감쇠 반경(m). 없으면 CAVE_LIGHT_R. 가로등처럼 작은 등은 작게 줘야
 *         「등 하나가 마을 반쪽을 물들이는」 일이 안 난다.
 *  rgb  — [r,g,b] 0..1. 없으면 CAVE_LIGHT_COLOR(발광 결정의 웜톤).
 *  gain — 세기 배수(기본 1). 밝기를 반경으로 대신 조절하면 「크게 하면 세진다」로 축이 엉킨다.
 */
export const setCaveLights = (list) => {
  const v = CAVE_LIGHTS.value;
  const g = CAVE_LIGHT_GATE.value;
  const rgb = CAVE_LIGHT_RGB.value;
  const base = CAVE_LIGHT_COLOR.value;
  const night = Math.min(1, Math.max(0, SKY_NIGHT.value));
  // ⚠ **빈 슬롯을 중간에 남기지 않는다.** 셰이더 바깥 분기가 `uCaveL[0].w > 0.01` 하나로
  //   광원 블록 전체를 켜고 끄기 때문에, 0번이 비면 3번에 든 광원이 통째로 안 그려진다.
  //   그래서 「켜질 것」만 골라 **앞에서부터 채운다**.
  let n = 0;
  for (let i = 0; i < list.length && n < CAVE_LIGHT_MAX; i += 1) {
    const l = list[i];
    if (!l) continue;
    // 지상 광원의 게이트가 곧 「밤에만 켠다」 — 낮에 지면이 타던 실사고(§14.8)의 처방이다.
    const gate = (l.pet ? Math.max(UNDER.value, night) : isUnderTerrain(l.x, l.y, l.z) ? 1 : night) * (l.gain ?? 1);
    // 낮의 지상 등은 기여가 0 이다. 슬롯을 주면 예산만 먹고, 무엇보다 **바깥 분기를 켜서**
    // 온 지형 픽셀이 dFdx 법선 계산 + K회 루프를 공짜로 내게 된다.
    if (gate < 0.004) continue;
    const rr = (l.r || CAVE_LIGHT_R) * (!l.pet && isUnderTerrain(l.x, l.y, l.z) ? CAVE_POOL_R : 1);
    v[n].set(l.x, l.y, l.z, rr);
    g[n] = gate;
    const c = l.rgb;
    rgb[n * 3] = c ? c[0] : base.r;
    rgb[n * 3 + 1] = c ? c[1] : base.g;
    rgb[n * 3 + 2] = c ? c[2] : base.b;
    n += 1;
  }
  for (let i = n; i < CAVE_LIGHT_MAX; i += 1) {
    v[i].set(0, -9999, 0, 0);        // w=0 → 셰이더가 건너뛴다
    g[i] = 0;
  }
};

// 「땅속인가」 — 플레이어 지하 상태과 지하 카메라(CameraRig.clampCameraUnderRoof)가 **같은 정의**를 쓴다.
// 두 정의가 갈라지면 "불은 켜졌는데 카메라는 아직 밖" 같은 어긋남이 난다.
// 노천 구덩이는 지표가 같이 내려가므로 거짓이고, 흙 밑 굴에 들어가야 참이다.
// 기준은 «그려진» 지면(4m 블록 윗면)이다. 1m 원본 높이는 경사 계단에서 윗면보다 수 m 높아
// 서 있기만 해도 땅속으로 읽힌다. 굴착 칸만 원본을 본다. 함수는 playerSurface 가 등록한다(순환 import 회피).
let _drawnGroundY = null;
export const setDrawnGroundFn = (fn) => { _drawnGroundY = fn; };
const groundRefY = (x, z) => {
  if (_drawnGroundY && !hfCarvedAt(x, z)) {
    const d = _drawnGroundY(x, z);
    if (Number.isFinite(d)) return d;
  }
  return hfSurfaceYAt(x, z);
};

export const isUnderTerrain = (x, y, z) => {
  const surf = groundRefY(x, z);
  return surf != null && y < surf - DEPTH_ON;
};

// ── 「굴 안에서는 지상 레이어를 끈다」의 **지오메트리 몫** ──────────
// UNDER(위)가 셰이더 항을 끄는 몫이라면, 이건 아예 안 그리는 몫이다.
//
// [왜 필요한가] 봉인 동굴 안 실측(FPS 10.2): 씬 삼각형 1,981,306 중 TOP TRIS 8줄이
//   **전부 지상**이었다 — 지형 타일 451,930 + 복셀 나무 549,343. 스캐터는 frustumCulled=false
//   에 3D 거리 컷(340m)만 있어서, 60m 지하로 내려가도 340m 구(球)는 거의 안 줄어든다.
//   귀환점 340m 안에 placed_objects 2,649행 = 인스턴스 29,426개가 암반 너머에서 그려졌다.
//   게다가 그리는 순서가 실내에 최악이다: 하늘돔(-1000) → 나무(0) → 물(3) → 지형(4).
//   **화면에 보이는 유일한 것(굴 벽)이 맨 마지막**이라 앞의 셋이 8.9M 픽셀을 먼저 칠한다.
//
// [왜 DEPTH_ON(1.6m)을 안 쓰나] 그 문턱은 **지하 전환용**이다. 1.6m 아래는 아직 굴 입구 안이고,
//   거기서 지상을 지우면 입구 구멍 너머 하늘·나무가 통째로 사라진 게 보인다. 지오메트리는
//   「입구가 안 보일 만큼 깊은가」를 물어야 하므로 문턱이 따로다 — 같은 값으로 묶으면
//   지하 임계값을 손볼 때마다 입구 연출이 같이 흔들린다.
// [16m 인 이유] 방 rim 천장고(CEIL_RIM 12m)보다 깊다 = 머리 위 암반이 내가 선 방보다 두껍다.
//   §14.10 의 18.4° 하강 계단으로는 수평 48m 를 들어와야 닿는 깊이라, 입구는 이미 점이다.
// [히스테리시스] 켜짐 16m / 꺼짐 12m — 문턱에 서서 걸을 때 지상이 깜빡이지 않게.
const DEPTH_CULL_ON = 16;   // 이보다 깊으면 지상 레이어를 끈다(m)
const DEPTH_CULL_OFF = 12;  // 이보다 얕아지면 다시 켠다(m)
const STALE_MS = 400;       // 이 시간 이상 판정이 안 갱신되면 「모른다」로 보고 켠다
let _surfaceHidden = false;
let _hiddenAt = 0;

/** 「지금 지상 레이어를 그릴 필요가 없나」 — ProceduralSky·WorldRivers 가 읽는다.
 *
 *  **모르면 그린다**(fail-open). 이 판정이 틀리는 두 방향은 대칭이 아니다 —
 *  못 껐으면 그냥 예전만큼 느린 거고, 잘못 껐으면 **세상이 사라진다.**
 *  ① 지표 모름(surf==null, 하이트필드 미로드) → depth=-1 → 어떤 문턱도 못 넘는다.
 *  ② 판정이 **멈춤**: updateUndergroundState 는 플레이어 프레임 루프가 돌린다. 사망 화면·워프 로딩으로
 *     그 컴포넌트가 잠깐 언마운트되면 마지막 값이 true 로 박제되고, 그동안 지상이 통째로
 *     검게 남는다. 그래서 값이 아니라 **값의 나이**를 같이 본다. */
export const surfaceHidden = () => (
  (_surfaceHidden && (performance.now() - _hiddenAt) < STALE_MS)
  || (_camHidden && (performance.now() - _camAt) < STALE_MS)
);

// ── 카메라 축 — 플레이어 루프가 안 돌 때도 답이 나오게 ────────────────
// 위 판정은 `updateUndergroundState`(플레이어 이동 루프)가 먹인다. 자유 시점 촬영·컷신처럼 **카메라만**
// 움직이는 자리에서는 그 루프가 안 돌아 400ms 뒤 fail-open 으로 떨어진다 — 굴 안인데 화면이
// 지상만큼 밝아진다(2026-09-21 에 검증 캡처가 통째로 이 증상이었다).
// 그래서 축을 하나 더 둔다: **둘 중 하나라도 굴 안이면 굴 안이다.** 3인칭으로 카메라만
// 밖에 나간 순간 지상이 깜빡이지 않게, 문턱은 플레이어 쪽과 같은 16/12m 히스테리시스다.
let _camHidden = false;
let _camAt = 0;

// 굴 어둠 전체 스위치. 끄면 종전과 픽셀 하나 안 다르다 — A/B 를 같은 빌드에서 하려고 둔다.
// ⚠ 이 상수가 여기 사는 이유: ProceduralSky 에 두면 AtmosphereController 가 그걸 import 해야
//   하는데 그 방향이 순환이라 값이 undefined 로 들어온다(그 상태로는 어둠이 통째로 안 걸린다).
//   caveLight 는 둘 다 이미 물고 있는 잎(leaf) 모듈이라 순환이 안 난다.
export const CAVE_DARK_ON = true;
// 굴 공기색 — 굴 안개(AtmosphereController)와 하늘돔이 꺼진 뒤 배경(ProceduralSky)이 «같은 객체»를 쓴다.
// 둘이 다르면 먼 벽이 어둠에 잠기지 않고 「색 커튼」이 된다. 아주 짙은 청록 — 검정보다 깊이가 읽힌다.
export const CAVE_AIR = new THREE.Color('#07131a');
// 지하에서 환경광·태양·반구광에 곱하는 계수. 0 이면 발광 블록 말고는 아무것도 안 보인다.
export const UNDER_LIGHT_K = 0.05;

/** 카메라 위치로 굴 안 여부를 먹인다 — AtmosphereController 가 매 프레임 호출한다. */
export const updateCameraDepth = (x, y, z) => {
  const surf = hfSurfaceYAt(x, z);
  if (surf == null) { _camHidden = false; return; }
  const d = surf - y;
  _camHidden = _camHidden ? d > DEPTH_CULL_OFF : d > DEPTH_CULL_ON;
  _camAt = performance.now();
};

// ── 하늘 가림 — 「땅속이냐」 대신 «머리 위 하늘이 얼마나 보이나» ─────────
// 머리 높이에서 하늘 쪽 광선 15개(천정 1 · 60° 6 · 30° 8)를 쏘아, 그려진 지면 아래로 들어가면 가린 것으로 센다.
// 가중치는 cos(천정각) — 하늘빛은 위에서 많이 온다. 결과 0(탁 트임)..1(사방 막힘).
// 입구에서는 반만 가려 반쯤 어둡고, 깊이 들어갈수록 어두워진다. 건물 지붕(복셀 시공물)은 보지 않는다.
const SKY_STEPS = [1.5, 3, 5, 8, 12, 17, 24, 34, 48];
const SKY_RAYS = (() => {
  const out = [{ dx: 0, dy: 1, dz: 0, w: 1 }];
  const ring = (elevDeg, n) => {
    const e = elevDeg * Math.PI / 180;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + elevDeg;
      out.push({ dx: Math.cos(e) * Math.cos(a), dy: Math.sin(e), dz: Math.cos(e) * Math.sin(a), w: Math.sin(e) });
    }
  };
  ring(60, 6); ring(30, 8);
  const sum = out.reduce((t, r) => t + r.w, 0);
  out.forEach((r) => { r.w /= sum; });
  return out;
})();
const SKY_EVERY_S = 0.15;
let _skyIn = 0;
let _skyCover = 0;
export const skyCoverAt = (x, y, z) => {
  let cover = 0;
  for (const r of SKY_RAYS) {
    for (const t of SKY_STEPS) {
      const px = x + r.dx * t, py = y + r.dy * t, pz = z + r.dz * t;
      const g = groundRefY(px, pz);
      if (g != null && Number.isFinite(g) && py < g - 0.25) { cover += r.w; break; }
    }
  }
  return cover;
};
// 가림 → 어둠. 언덕 골짜기처럼 옆만 조금 가린 자리는 거의 안 어둡고, 하늘 대부분이 막혀야 어두워진다.
const darkOfCover = (c) => {
  const t = Math.min(1, Math.max(0, (c - 0.35) / 0.55));
  return t * t * (3 - 2 * t);
};

// 플레이어 이동 루프가 매 프레임 부른다(usePlayerMovement·usePlayerFlight).
export const updateUndergroundState = (x, y, z, dt = 0.016) => {
  const surf = hfSurfaceYAt(x, z);
  _skyIn -= dt;
  if (_skyIn <= 0) { _skyIn = SKY_EVERY_S; _skyCover = skyCoverAt(x, y + 1.5, z); }
  // 굴착 칸 안은 하늘 광선이 원본 지표에 막혀 곧바로 1 이 된다 — 판정과 같은 기준이다
  const target = darkOfCover(_skyCover);
  UNDER.value += (target - UNDER.value) * Math.min(1, dt * FADE * 0.6);
  if (typeof window !== 'undefined') window.__skyCover = { cover: +_skyCover.toFixed(2), dark: +UNDER.value.toFixed(2) };

  const depth = surf != null ? surf - y : -1;   // 지표 모름 → -1 → 어떤 문턱도 못 넘는다
  _surfaceHidden = depth >= (_surfaceHidden ? DEPTH_CULL_OFF : DEPTH_CULL_ON);
  _hiddenAt = performance.now();               // 「이 판정은 방금 것」 — surfaceHidden() 이 나이를 본다
};
